import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import {
  Zap, Link2, RefreshCw, Brain, Mail, History,
  CheckCircle2, XCircle, AlertTriangle, ExternalLink, Eye, Edit2, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ==================== Mock Data ====================

const BANK_CONNECTIONS = [
  { fund: 'Smith Family Super Fund', bank: 'Commonwealth Bank', bsb: '062-000', account: '1234 5678', lastSync: '17/04/2026 09:15', status: 'connected' as const },
  { fund: 'Johnson Retirement Fund', bank: 'Westpac', bsb: '032-001', account: '9876 5432', lastSync: '17/04/2026 08:42', status: 'connected' as const },
  { fund: 'ABC Pty Ltd Super Fund', bank: 'ANZ', bsb: '013-200', account: '5555 1234', lastSync: '10/04/2026 14:30', status: 'disconnected' as const },
  { fund: 'Garcia & Partners Super Fund', bank: 'NAB', bsb: '082-100', account: '3333 7890', lastSync: '16/04/2026 22:00', status: 'connected' as const },
];

const RECENT_TRANSACTIONS = [
  { date: '17/04/2026', fund: 'Smith Family Super Fund', description: 'CommSec Trade Settlement', amount: -12450.00, type: 'Debit' },
  { date: '16/04/2026', fund: 'Johnson Retirement Fund', description: 'Employer SG Contribution', amount: 2875.00, type: 'Credit' },
  { date: '16/04/2026', fund: 'Garcia & Partners Super Fund', description: 'Pension Payment - R Garcia', amount: -4200.00, type: 'Debit' },
  { date: '15/04/2026', fund: 'Smith Family Super Fund', description: 'Dividend - BHP Group', amount: 1340.00, type: 'Credit' },
  { date: '15/04/2026', fund: 'Johnson Retirement Fund', description: 'Rental Income - 42 Collins St', amount: 3800.00, type: 'Credit' },
  { date: '14/04/2026', fund: 'Garcia & Partners Super Fund', description: 'Insurance Premium', amount: -890.00, type: 'Debit' },
];

const BROKER_FEEDS = [
  { name: 'CommSec', status: 'connected' as const, lastSync: '17/04/2026 09:00', trades: 12 },
  { name: 'NAB Trade', status: 'connected' as const, lastSync: '17/04/2026 08:30', trades: 5 },
  { name: 'Interactive Brokers', status: 'not-configured' as const, lastSync: '-', trades: 0 },
];

const RECENT_TRADES = [
  { date: '16/04/2026', fund: 'Smith Family Super Fund', security: 'BHP.AX', action: 'Buy' as const, qty: 200, price: 45.20, brokerage: 19.95 },
  { date: '15/04/2026', fund: 'Johnson Retirement Fund', security: 'CBA.AX', action: 'Sell' as const, qty: 100, price: 112.50, brokerage: 19.95 },
  { date: '14/04/2026', fund: 'Smith Family Super Fund', security: 'VAS.AX', action: 'Buy' as const, qty: 500, price: 92.10, brokerage: 9.95 },
  { date: '13/04/2026', fund: 'Garcia & Partners Super Fund', security: 'WBC.AX', action: 'Buy' as const, qty: 300, price: 26.80, brokerage: 19.95 },
  { date: '12/04/2026', fund: 'Johnson Retirement Fund', security: 'CSL.AX', action: 'Sell' as const, qty: 50, price: 298.00, brokerage: 19.95 },
];

const UNMATCHED_ITEMS = [
  { id: 1, date: '16/04/2026', description: 'Transfer from CBA Savings', amount: 5000.00, suggestedMatch: 'Member Contribution - J Smith', confidence: 85 },
  { id: 2, date: '15/04/2026', description: 'BPAY Payment 123456', amount: -1200.00, suggestedMatch: 'ATO PAYG Instalment', confidence: 72 },
  { id: 3, date: '14/04/2026', description: 'EFT Credit - Johnson', amount: 3500.00, suggestedMatch: 'Salary Sacrifice - M Johnson', confidence: 91 },
  { id: 4, date: '13/04/2026', description: 'Direct Debit - XYZ Ins', amount: -450.00, suggestedMatch: 'Insurance Premium', confidence: 95 },
  { id: 5, date: '12/04/2026', description: 'Cheque Deposit', amount: 10000.00, suggestedMatch: 'Non-Concessional Contribution', confidence: 60 },
];

const CONTRIBUTION_CLASSIFICATIONS = [
  { member: 'John Smith', amount: 27500, detectedType: 'Superannuation Guarantee', confidence: 98, status: 'confirmed' as const },
  { member: 'Jane Smith', amount: 15000, detectedType: 'Salary Sacrifice', confidence: 94, status: 'confirmed' as const },
  { member: 'Mark Johnson', amount: 110000, detectedType: 'Non-Concessional', confidence: 87, status: 'needs-review' as const },
  { member: 'Sarah Johnson', amount: 300000, detectedType: 'Downsizer', confidence: 92, status: 'confirmed' as const },
  { member: 'Peter Johnson', amount: 27500, detectedType: 'SG', confidence: 96, status: 'confirmed' as const },
  { member: 'Rosa Garcia', amount: 5000, detectedType: 'Personal Deductible', confidence: 78, status: 'needs-review' as const },
  { member: 'Carlos Garcia', amount: 3000, detectedType: 'Spouse Contribution', confidence: 82, status: 'needs-review' as const },
];

const EMAIL_TEMPLATES = [
  { name: 'Annual Review Invitation', description: 'Invite members to their annual SMSF review meeting', lastUsed: '01/03/2026' },
  { name: 'Contribution Confirmation', description: 'Confirm receipt and allocation of member contributions', lastUsed: '15/04/2026' },
  { name: 'Pension Payment Notice', description: 'Notify members of upcoming pension payment details', lastUsed: '01/04/2026' },
  { name: 'Document Request', description: 'Request outstanding documents from trustees', lastUsed: '10/04/2026' },
  { name: 'Audit Completion', description: 'Notify trustees that the fund audit has been completed', lastUsed: '28/02/2026' },
  { name: 'Year-End Reminder', description: 'Remind members of year-end actions and deadlines', lastUsed: '15/05/2025' },
];

const AUDIT_TRAIL = [
  { timestamp: '17/04/2026 09:15', user: 'System', action: 'Bank Sync', fund: 'Smith Family Super Fund', details: 'Synced 3 new transactions from CBA' },
  { timestamp: '17/04/2026 08:42', user: 'System', action: 'Bank Sync', fund: 'Johnson Retirement Fund', details: 'Synced 5 new transactions from Westpac' },
  { timestamp: '16/04/2026 16:30', user: 'Sarah T.', action: 'Contribution Classified', fund: 'Garcia & Partners Super Fund', details: 'Classified $5,000 as Personal Deductible for Rosa Garcia' },
  { timestamp: '16/04/2026 14:20', user: 'System', action: 'Auto-Reconciliation', fund: 'Smith Family Super Fund', details: 'Matched 8 of 11 transactions automatically' },
  { timestamp: '16/04/2026 11:00', user: 'Michael B.', action: 'Email Sent', fund: 'Johnson Retirement Fund', details: 'Sent Contribution Confirmation to Mark Johnson' },
  { timestamp: '15/04/2026 17:45', user: 'Sarah T.', action: 'Document Upload', fund: 'ABC Pty Ltd Super Fund', details: 'Uploaded signed Investment Strategy document' },
  { timestamp: '15/04/2026 10:00', user: 'System', action: 'Trade Imported', fund: 'Smith Family Super Fund', details: 'Imported 2 trades from CommSec' },
  { timestamp: '14/04/2026 09:30', user: 'Michael B.', action: 'Pension Payment', fund: 'Garcia & Partners Super Fund', details: 'Processed pension payment of $4,200 for R Garcia' },
];

// ==================== Component ====================

export default function SMSFAutomationPage() {
  const [auditFundFilter, setAuditFundFilter] = useState('all');
  const [auditActionFilter, setAuditActionFilter] = useState('all');

  // --- API Queries ---
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const firstFundId = fundsData?.[0]?.id;

  const { data: apiContributions, isLoading: contribLoading } = useQuery({
    queryKey: ['smsf-fund-contributions', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/contributions`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  const { data: apiCompliance, isLoading: complianceLoading } = useQuery({
    queryKey: ['smsf-fund-compliance', firstFundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${firstFundId}/compliance`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!firstFundId,
  });

  // Use API data with mock fallback
  const contributionClassifications = apiContributions || CONTRIBUTION_CLASSIFICATIONS;
  const auditTrailData = apiCompliance?.auditTrail || AUDIT_TRAIL;
  const isDataLoading = fundsLoading || contribLoading || complianceLoading;

  const matchedPct = 73;
  const unmatchedCount = UNMATCHED_ITEMS.length;

  const filteredAuditTrail = auditTrailData.filter((entry: any) => {
    if (auditFundFilter !== 'all' && entry.fund !== auditFundFilter) return false;
    if (auditActionFilter !== 'all' && entry.action !== auditActionFilter) return false;
    return true;
  });

  const uniqueFunds = [...new Set(auditTrailData.map((e: any) => e.fund))] as string[];
  const uniqueActions = [...new Set(auditTrailData.map((e: any) => e.action))] as string[];

  return (
    <div className="space-y-6">
      <PageHeader icon={Zap} title="SMSF Automation & Integrations" />

      {isDataLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      <Tabs defaultValue="bank-feeds">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="bank-feeds"><Link2 className="h-3.5 w-3.5 mr-1.5" />Bank Feeds</TabsTrigger>
          <TabsTrigger value="broker-feeds"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Broker Feeds</TabsTrigger>
          <TabsTrigger value="auto-recon"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Auto-Reconciliation</TabsTrigger>
          <TabsTrigger value="contrib-classifier"><Brain className="h-3.5 w-3.5 mr-1.5" />Contribution Classifier</TabsTrigger>
          <TabsTrigger value="email-templates"><Mail className="h-3.5 w-3.5 mr-1.5" />Email Templates</TabsTrigger>
          <TabsTrigger value="audit-trail"><History className="h-3.5 w-3.5 mr-1.5" />Audit Trail</TabsTrigger>
        </TabsList>

        {/* ===== Bank Feeds ===== */}
        <TabsContent value="bank-feeds" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BANK_CONNECTIONS.map((conn, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">{conn.fund}</p>
                      <p className="text-xs text-muted-foreground mt-1">{conn.bank}</p>
                      <p className="text-xs text-muted-foreground">BSB: {conn.bsb} | Account: {conn.account}</p>
                      <p className="text-xs text-muted-foreground mt-1">Last sync: {conn.lastSync}</p>
                    </div>
                    <Badge variant={conn.status === 'connected' ? 'default' : 'destructive'}>
                      {conn.status === 'connected' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                      {conn.status === 'connected' ? 'Connected' : 'Disconnected'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={() => toast.info('Connect Bank flow coming soon')}>
              <Link2 className="h-4 w-4 mr-1.5" />Connect Bank
            </Button>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-4 font-medium">Description</th>
                      <th className="pb-2 pr-4 font-medium text-right">Amount</th>
                      <th className="pb-2 font-medium">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RECENT_TRANSACTIONS.map((tx, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 text-xs text-muted-foreground">{tx.date}</td>
                        <td className="py-2.5 pr-4 text-xs">{tx.fund}</td>
                        <td className="py-2.5 pr-4">{tx.description}</td>
                        <td className={cn('py-2.5 pr-4 text-right font-medium', tx.amount >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                          {tx.amount >= 0 ? '+' : ''}${Math.abs(tx.amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5">
                          <Badge variant={tx.type === 'Credit' ? 'default' : 'secondary'}>{tx.type}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Broker Feeds ===== */}
        <TabsContent value="broker-feeds" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BROKER_FEEDS.map((broker, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-semibold">{broker.name}</p>
                    <Badge variant={broker.status === 'connected' ? 'default' : 'outline'}>
                      {broker.status === 'connected' ? 'Connected' : 'Not Configured'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Last sync: {broker.lastSync}</p>
                  <p className="text-xs text-muted-foreground">Trades this month: {broker.trades}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent Trades</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 pr-4 font-medium">Security</th>
                      <th className="pb-2 pr-4 font-medium">Action</th>
                      <th className="pb-2 pr-4 font-medium text-right">Quantity</th>
                      <th className="pb-2 pr-4 font-medium text-right">Price</th>
                      <th className="pb-2 font-medium text-right">Brokerage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RECENT_TRADES.map((trade, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 text-xs text-muted-foreground">{trade.date}</td>
                        <td className="py-2.5 pr-4 text-xs">{trade.fund}</td>
                        <td className="py-2.5 pr-4 font-mono text-xs">{trade.security}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant={trade.action === 'Buy' ? 'default' : 'secondary'}>{trade.action}</Badge>
                        </td>
                        <td className="py-2.5 pr-4 text-right">{trade.qty.toLocaleString()}</td>
                        <td className="py-2.5 pr-4 text-right">${trade.price.toFixed(2)}</td>
                        <td className="py-2.5 text-right">${trade.brokerage.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Auto-Reconciliation ===== */}
        <TabsContent value="auto-recon" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-xs text-muted-foreground">Transactions Matched</p>
                <p className="text-3xl font-bold text-emerald-600">{matchedPct}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-xs text-muted-foreground">Unmatched Items</p>
                <p className="text-3xl font-bold text-amber-600">{unmatchedCount}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-xs text-muted-foreground">Auto-Match Rate (30 days)</p>
                <p className="text-3xl font-bold text-blue-600">86%</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Unmatched Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Description</th>
                      <th className="pb-2 pr-4 font-medium text-right">Amount</th>
                      <th className="pb-2 pr-4 font-medium">Suggested Match</th>
                      <th className="pb-2 pr-4 font-medium text-center">Confidence</th>
                      <th className="pb-2 font-medium text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {UNMATCHED_ITEMS.map(item => (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 text-xs text-muted-foreground">{item.date}</td>
                        <td className="py-2.5 pr-4">{item.description}</td>
                        <td className={cn('py-2.5 pr-4 text-right font-medium', item.amount >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                          {item.amount >= 0 ? '+' : '-'}${Math.abs(item.amount).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 pr-4 text-xs">{item.suggestedMatch}</td>
                        <td className="py-2.5 pr-4 text-center">
                          <Badge variant={item.confidence >= 90 ? 'default' : item.confidence >= 75 ? 'secondary' : 'outline'}>
                            {item.confidence}%
                          </Badge>
                        </td>
                        <td className="py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" onClick={() => toast.success(`Approved: ${item.suggestedMatch}`)}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => toast.info(`Rejected: ${item.suggestedMatch}`)}>
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Contribution Classifier ===== */}
        <TabsContent value="contrib-classifier" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">AI Contribution Classification</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Member</th>
                      <th className="pb-2 pr-4 font-medium text-right">Amount</th>
                      <th className="pb-2 pr-4 font-medium">Detected Type</th>
                      <th className="pb-2 pr-4 font-medium text-center">Confidence</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contributionClassifications.map((c: any, i: number) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 font-medium">{c.member}</td>
                        <td className="py-2.5 pr-4 text-right">${c.amount.toLocaleString('en-AU')}</td>
                        <td className="py-2.5 pr-4">{c.detectedType}</td>
                        <td className="py-2.5 pr-4 text-center">
                          <Badge variant={c.confidence >= 90 ? 'default' : c.confidence >= 80 ? 'secondary' : 'outline'}>
                            {c.confidence}%
                          </Badge>
                        </td>
                        <td className="py-2.5">
                          {c.status === 'confirmed' ? (
                            <Badge variant="default"><CheckCircle2 className="h-3 w-3 mr-1" />Confirmed</Badge>
                          ) : (
                            <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Needs Review</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Email Templates ===== */}
        <TabsContent value="email-templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {EMAIL_TEMPLATES.map((tpl, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-2">
                    <Mail className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  </div>
                  <p className="text-sm font-semibold mb-1">{tpl.name}</p>
                  <p className="text-xs text-muted-foreground mb-3">{tpl.description}</p>
                  <p className="text-xs text-muted-foreground mb-3">Last used: {tpl.lastUsed}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.info(`Preview: ${tpl.name}`)}>
                      <Eye className="h-3 w-3 mr-1" />Preview
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toast.info(`Edit: ${tpl.name}`)}>
                      <Edit2 className="h-3 w-3 mr-1" />Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ===== Audit Trail ===== */}
        <TabsContent value="audit-trail" className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <select
              className="text-sm border rounded-md px-3 py-1.5 bg-background"
              value={auditFundFilter}
              onChange={e => setAuditFundFilter(e.target.value)}
            >
              <option value="all">All Funds</option>
              {uniqueFunds.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select
              className="text-sm border rounded-md px-3 py-1.5 bg-background"
              value={auditActionFilter}
              onChange={e => setAuditActionFilter(e.target.value)}
            >
              <option value="all">All Actions</option>
              {uniqueActions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Timestamp</th>
                      <th className="pb-2 pr-4 font-medium">User</th>
                      <th className="pb-2 pr-4 font-medium">Action</th>
                      <th className="pb-2 pr-4 font-medium">Fund</th>
                      <th className="pb-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAuditTrail.map((entry: any, i: number) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2.5 pr-4 text-xs text-muted-foreground whitespace-nowrap">{entry.timestamp}</td>
                        <td className="py-2.5 pr-4 text-xs font-medium">{entry.user}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant="secondary">{entry.action}</Badge>
                        </td>
                        <td className="py-2.5 pr-4 text-xs">{entry.fund}</td>
                        <td className="py-2.5 text-xs text-muted-foreground">{entry.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
